import { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Navigate } from 'react-router-dom';
import axios from 'axios';
import {
	BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid,
	Tooltip, ResponsiveContainer, ReferenceLine,
} from 'recharts';
import {
	BarChart3, Search, X, ChevronDown, TrendingUp, TrendingDown,
	Target, Activity, Loader2,
} from 'lucide-react';
import { getUserPermissions, isAuthorized } from '../utils/Permissions';
import { cn } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from './ui/Card';
import { Button } from './ui/Button';
import { Badge } from './ui/Badge';
import { Input } from './ui/Input';
import { Popover, PopoverContent, PopoverTrigger } from './ui/Popover';
import { Checkbox } from './ui/Checkbox';
import {
	Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from './ui/Table';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from './ui/Chart';
import CalendarHeatmap from './metrics/CalendarHeatmap';
import WeeklyPnlTiles from './metrics/WeeklyPnlTiles';
import MonthlyPnlTiles from './metrics/MonthlyPnlTiles';
import '../styles/theme.css';

const formatDateForInput = (date) => {
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, '0');
	const day = String(date.getDate()).padStart(2, '0');
	return `${year}-${month}-${day}`;
};

const formatDollars = (value) =>
	new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value || 0);

const formatPct = (value) =>
	`${((value || 0) * 100).toFixed(1)}%`;

// Build cumulative P&L from daily data
const buildCumulativePnl = (dailyData) => {
	if (!dailyData?.length) return [];
	let cumulative = 0;
	return dailyData.map((d) => {
		cumulative += d.profit || 0;
		return { date: d.date, cumulative: Math.round(cumulative * 100) / 100 };
	});
};

/* ─── KPI Card ───────────────────────────────────────────── */
function KpiCard({ title, value, subtitle, icon: Icon, variant = 'default' }) {
	return (
		<Card className="gap-4">
			<CardHeader className="pb-0">
				<CardTitle className="flex items-center text-sm text-muted-foreground">
					{Icon && <Icon className="mr-2 size-4" />}
					{title}
				</CardTitle>
			</CardHeader>
			<CardContent>
				<div className={cn(
					'text-2xl font-bold tabular-nums',
					variant === 'profit' && 'text-green-600',
					variant === 'loss' && 'text-red-600',
				)}>
					{value}
				</div>
				{subtitle && <p className="mt-1 text-xs text-muted-foreground">{subtitle}</p>}
			</CardContent>
		</Card>
	);
}

/* ─── Main Component ─────────────────────────────────────── */
export const AlgoMetrics = () => {
	const { getAccessTokenSilently, user, isAuthenticated } = useAuth0();

	const [permissionsLoaded, setPermissionsLoaded] = useState(false);

	// Data
	const [metrics, setMetrics] = useState(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);

	// Filters
	const today = new Date();
	const thirtyDaysAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
	const [startDate, setStartDate] = useState(formatDateForInput(thirtyDaysAgo));
	const [endDate, setEndDate] = useState(formatDateForInput(today));
	const [filterAlgos, setFilterAlgos] = useState([]);
	const [filterTickers, setFilterTickers] = useState([]);


	// Load permissions
	useEffect(() => {
		const loadPermissions = async () => {
			if (isAuthenticated && user?.sub) {
				const token = await getAccessTokenSilently();
				const userId = user.sub.split('|')[1];
				await getUserPermissions(token, userId);
				setPermissionsLoaded(true);
			}
		};
		loadPermissions();
	}, [isAuthenticated, user, getAccessTokenSilently]);

	// Auth guard
	if (!permissionsLoaded) {
		return (
			<div className="flex items-center justify-center" style={{ minHeight: '200px' }}>
				<Loader2 className="size-6 animate-spin text-muted-foreground" />
			</div>
		);
	}

	if (!isAuthorized('admin')) {
		return <Navigate to="/connect" replace />;
	}

	const getAuthHeaders = async () => {
		const token = await getAccessTokenSilently();
		const userId = user.sub.split('|')[1];
		return {
			headers: { Authorization: `Bearer ${token}` },
			params: { uid: userId },
		};
	};

	const fetchMetrics = async () => {
		setLoading(true);
		setError(null);
		try {
			const config = await getAuthHeaders();
			const queryParams = {
				...config.params,
				startDate,
				endDate,
				...(filterAlgos.length > 0 && { algorithms: filterAlgos.join(',') }),
				...(filterTickers.length > 0 && { tickers: filterTickers.join(',') }),
			};
			const response = await axios.get(
				`${process.env.REACT_APP_SERVERLESS_API_URL}/api/v2/metrics`,
				{ headers: config.headers, params: queryParams }
			);
			setMetrics(response.data?.data || response.data);
		} catch (err) {
			setError(err.response?.data?.error || err.message);
		} finally {
			setLoading(false);
		}
	};

	const toggleFilter = (setter, value) => {
		setter((prev) =>
			prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
		);
	};

	const clearFilters = () => {
		setFilterAlgos([]);
		setFilterTickers([]);
		setStartDate(formatDateForInput(thirtyDaysAgo));
		setEndDate(formatDateForInput(today));
	};

	const hasActiveFilters = filterAlgos.length > 0 || filterTickers.length > 0;

	// Derived data
	const summary = metrics?.summary || {};
	const dailyPnl = metrics?.dailyPnl || [];
	const rawAlgoOptions = metrics?.algoOptions || [];
	const algoOptions = rawAlgoOptions.map((o) => (typeof o === 'string' ? { key: o, label: o } : o));
	const rawTickerOptions = metrics?.tickerOptions || [];
	const tickerOptions = rawTickerOptions.map((o) => (typeof o === 'string' ? { key: o, label: o } : o));
	const pnlByAlgo = metrics?.pnlByAlgo || [];
	const pnlByTicker = metrics?.pnlByTicker || [];
	const pnlByAlgoTicker = metrics?.pnlByAlgoTicker || [];

	const sortedDailyPnl = [...dailyPnl].sort((a, b) => a.date.localeCompare(b.date));
	const cumulativePnl = buildCumulativePnl(sortedDailyPnl);

	const hasMetrics = summary.totalTrades > 0;
	const netPnlVariant = (summary.netPnl || 0) >= 0 ? 'profit' : 'loss';

	return (
		<div className="alm-metrics space-y-4 p-4 md:p-6" style={{ maxWidth: '1400px', margin: '0 auto' }}>
			{/* ── Toolbar ───────────────────────────────────── */}
			<div className="flex flex-wrap items-center gap-2">
				<h1 className="mr-2 text-xl font-bold text-foreground">Algo Metrics</h1>

				{/* Algo filter */}
				{algoOptions.length > 0 && (
					<Popover>
						<PopoverTrigger asChild>
							<Button variant="outline" size="sm" className="h-8 w-[140px] justify-between text-xs">
								<span className="truncate">
									{filterAlgos.length === 0
										? 'All algos'
										: filterAlgos.length === 1
											? (algoOptions.find((o) => o.key === filterAlgos[0])?.label || filterAlgos[0])
											: `${filterAlgos.length} algos`}
								</span>
								<ChevronDown className="ml-1.5 size-3 shrink-0" />
							</Button>
						</PopoverTrigger>
						<PopoverContent className="w-52 p-2" align="start">
							<div className="max-h-56 space-y-0.5 overflow-y-auto">
								{algoOptions.map((opt) => (
									<label
										key={opt.key}
										className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-xs hover:bg-accent"
									>
										<Checkbox
											checked={filterAlgos.includes(opt.key)}
											onCheckedChange={() => toggleFilter(setFilterAlgos, opt.key)}
										/>
										{opt.label}
									</label>
								))}
							</div>
						</PopoverContent>
					</Popover>
				)}

				{/* Ticker filter */}
				{tickerOptions.length > 0 && (
					<Popover>
						<PopoverTrigger asChild>
							<Button variant="outline" size="sm" className="h-8 w-[130px] justify-between text-xs">
								<span className="truncate">
									{filterTickers.length === 0
										? 'All tickers'
										: filterTickers.length === 1
											? (tickerOptions.find((o) => o.key === filterTickers[0])?.label || filterTickers[0])
											: `${filterTickers.length} tickers`}
								</span>
								<ChevronDown className="ml-1.5 size-3 shrink-0" />
							</Button>
						</PopoverTrigger>
						<PopoverContent className="w-44 p-2" align="start">
							<div className="max-h-56 space-y-0.5 overflow-y-auto">
								{tickerOptions.map((opt) => (
									<label
										key={opt.key}
										className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-xs hover:bg-accent"
									>
										<Checkbox
											checked={filterTickers.includes(opt.key)}
											onCheckedChange={() => toggleFilter(setFilterTickers, opt.key)}
										/>
										{opt.label}
									</label>
								))}
							</div>
						</PopoverContent>
					</Popover>
				)}

				<Input
					type="date"
					className="h-8 w-[140px] text-xs"
					value={startDate}
					onChange={(e) => setStartDate(e.target.value)}
				/>
				<Input
					type="date"
					className="h-8 w-[140px] text-xs"
					value={endDate}
					onChange={(e) => setEndDate(e.target.value)}
				/>

				<Button size="sm" className="h-8 text-xs" onClick={fetchMetrics} disabled={loading}>
					{loading ? (
						<Loader2 className="mr-1 size-3 animate-spin" />
					) : (
						<Search className="mr-1 size-3" />
					)}
					{loading ? 'Loading...' : 'Apply'}
				</Button>

				{hasActiveFilters && (
					<Button variant="ghost" size="sm" className="h-8 text-xs" onClick={clearFilters}>
						<X className="mr-1 size-3" />
						Clear
					</Button>
				)}
			</div>

			{/* ── Content ───────────────────────────────────── */}
			{error && (
				<div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
					{error}
				</div>
			)}

			{loading ? (
				<div className="flex items-center justify-center py-20">
					<Loader2 className="size-8 animate-spin text-muted-foreground" />
				</div>
			) : hasMetrics ? (
				<div className="space-y-6">
					{/* ── KPI Cards ──────────────────────────── */}
					<div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
						<KpiCard
							title="Net P&L"
							value={formatDollars(summary.netPnl)}
							subtitle={`${summary.totalTrades || 0} trades`}
							icon={summary.netPnl >= 0 ? TrendingUp : TrendingDown}
							variant={netPnlVariant}
						/>
						<KpiCard
							title="Win Rate"
							value={formatPct(summary.winRate)}
							subtitle={`${summary.wins || 0}W / ${summary.losses || 0}L`}
							icon={Target}
						/>
						<KpiCard
							title="Profit Factor"
							value={(summary.profitFactor || 0).toFixed(2)}
							subtitle="Gross profit / gross loss"
							icon={Activity}
						/>
						<KpiCard
							title="Max Drawdown"
							value={formatDollars(summary.maxDrawdown)}
							icon={TrendingDown}
							variant="loss"
						/>
					</div>

					{/* ── Charts ──────────────────────────── */}
					<div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
						<Card>
							<CardHeader>
								<CardTitle className="text-sm">Cumulative P&L</CardTitle>
							</CardHeader>
							<CardContent>
								<ChartContainer config={{ cumulative: { label: 'Cumulative P&L', color: 'hsl(var(--chart-1))' } }} className="h-[300px]">
									<LineChart data={cumulativePnl}>
										<CartesianGrid strokeDasharray="3 3" />
										<XAxis dataKey="date" tick={{ fontSize: 11 }} />
										<YAxis tick={{ fontSize: 11 }} />
										<ChartTooltip content={<ChartTooltipContent formatter={(v) => formatDollars(v)} />} />
										<ReferenceLine y={0} stroke="hsl(var(--border))" />
										<Line type="monotone" dataKey="cumulative" stroke="hsl(var(--chart-1))" strokeWidth={2} dot={false} />
									</LineChart>
								</ChartContainer>
							</CardContent>
						</Card>

						<Card>
							<CardHeader>
								<CardTitle className="text-sm">Daily P&L</CardTitle>
							</CardHeader>
							<CardContent>
								<ChartContainer config={{ profit: { label: 'P&L', color: 'hsl(var(--chart-2))' } }} className="h-[300px]">
									<BarChart data={sortedDailyPnl}>
										<CartesianGrid strokeDasharray="3 3" />
										<XAxis dataKey="date" tick={{ fontSize: 11 }} />
										<YAxis tick={{ fontSize: 11 }} />
										<ChartTooltip content={<ChartTooltipContent formatter={(v) => formatDollars(v)} />} />
										<ReferenceLine y={0} stroke="hsl(var(--border))" />
										<Bar dataKey="profit" radius={[4, 4, 0, 0]} fill="hsl(var(--chart-2))" />
									</BarChart>
								</ChartContainer>
							</CardContent>
						</Card>
					</div>

					{/* ── Calendar & Period Tiles ─────────────── */}
					<CalendarHeatmap data={sortedDailyPnl} />

					<div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
						<WeeklyPnlTiles data={sortedDailyPnl} />
						<MonthlyPnlTiles data={sortedDailyPnl} />
					</div>

					{/* ── Breakdown Tables ────────────────────── */}
					<div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
						{/* By Algo */}
						{pnlByAlgo.length > 0 && (
							<Card>
								<CardHeader>
									<CardTitle className="text-sm">P&L by Algorithm</CardTitle>
								</CardHeader>
								<CardContent>
									<Table>
										<TableHeader>
											<TableRow>
												<TableHead>Algorithm</TableHead>
												<TableHead className="text-right">Trades</TableHead>
												<TableHead className="text-right">Win %</TableHead>
												<TableHead className="text-right">Net P&L</TableHead>
											</TableRow>
										</TableHeader>
										<TableBody>
											{pnlByAlgo.map((row, idx) => (
												<TableRow key={row.algo || row.key || idx}>
													<TableCell className="font-medium">{row.algo || row.label || row.key}</TableCell>
													<TableCell className="text-right">{row.trades}</TableCell>
													<TableCell className="text-right">{formatPct(row.winRate)}</TableCell>
													<TableCell className="text-right">
														<Badge variant={row.netPnl >= 0 ? 'profit' : 'loss'}>
															{formatDollars(row.netPnl)}
														</Badge>
													</TableCell>
												</TableRow>
											))}
										</TableBody>
									</Table>
								</CardContent>
							</Card>
						)}

						{/* By Ticker */}
						{pnlByTicker.length > 0 && (
							<Card>
								<CardHeader>
									<CardTitle className="text-sm">P&L by Ticker</CardTitle>
								</CardHeader>
								<CardContent>
									<Table>
										<TableHeader>
											<TableRow>
												<TableHead>Ticker</TableHead>
												<TableHead className="text-right">Trades</TableHead>
												<TableHead className="text-right">Win %</TableHead>
												<TableHead className="text-right">Net P&L</TableHead>
											</TableRow>
										</TableHeader>
										<TableBody>
											{pnlByTicker.map((row, idx) => (
												<TableRow key={row.ticker || row.key || idx}>
													<TableCell className="font-medium">{row.ticker || row.label || row.key}</TableCell>
													<TableCell className="text-right">{row.trades}</TableCell>
													<TableCell className="text-right">{formatPct(row.winRate)}</TableCell>
													<TableCell className="text-right">
														<Badge variant={row.netPnl >= 0 ? 'profit' : 'loss'}>
															{formatDollars(row.netPnl)}
														</Badge>
													</TableCell>
												</TableRow>
											))}
										</TableBody>
									</Table>
								</CardContent>
							</Card>
						)}
					</div>

					{/* ── Algo + Ticker Breakdown ────────────── */}
					{pnlByAlgoTicker.length > 0 && (
						<Card>
							<CardHeader>
								<CardTitle className="text-sm">P&L by Algorithm &amp; Ticker</CardTitle>
							</CardHeader>
							<CardContent>
								<Table>
									<TableHeader>
										<TableRow>
											<TableHead>Algorithm</TableHead>
											<TableHead>Ticker</TableHead>
											<TableHead className="text-right">Trades</TableHead>
											<TableHead className="text-right">Wins</TableHead>
											<TableHead className="text-right">Losses</TableHead>
											<TableHead className="text-right">Win %</TableHead>
											<TableHead className="text-right">Avg Win</TableHead>
											<TableHead className="text-right">Avg Loss</TableHead>
											<TableHead className="text-right">Net P&L</TableHead>
										</TableRow>
									</TableHeader>
									<TableBody>
										{pnlByAlgoTicker.map((row, idx) => (
											<TableRow key={`${row.algo}-${row.ticker}-${idx}`}>
												<TableCell className="font-medium">{row.algo}</TableCell>
												<TableCell>{row.ticker}</TableCell>
												<TableCell className="text-right">{row.trades}</TableCell>
												<TableCell className="text-right text-emerald-600">{row.wins}</TableCell>
												<TableCell className="text-right text-red-600">{row.losses}</TableCell>
												<TableCell className="text-right">{formatPct(row.winRate)}</TableCell>
												<TableCell className="text-right">{formatDollars(row.avgWin)}</TableCell>
												<TableCell className="text-right">{formatDollars(row.avgLoss)}</TableCell>
												<TableCell className="text-right">
													<Badge variant={row.netPnl >= 0 ? 'profit' : 'loss'}>
														{formatDollars(row.netPnl)}
													</Badge>
												</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</CardContent>
						</Card>
					)}
				</div>
			) : metrics ? (
				/* No data state */
				<div className="py-16 text-center">
					<BarChart3 className="mx-auto mb-4 size-12 opacity-40" />
					<h3 className="mb-2 text-lg font-medium text-foreground">No Metrics Data</h3>
					<p className="text-sm text-muted-foreground">
						No algo signals found for the selected date range and filters.
					</p>
				</div>
			) : (
				/* Initial state — no query yet */
				<div className="py-16 text-center">
					<BarChart3 className="mx-auto mb-4 size-12 opacity-40" />
					<h3 className="mb-2 text-lg font-medium text-foreground">Algo Performance Metrics</h3>
					<p className="text-sm text-muted-foreground">
						Select a date range and click Apply to view algo performance data.
					</p>
				</div>
			)}
		</div>
	);
};
